import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { CheckSquare, Square, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { formatDueLabel } from "./AddTaskDrawer";

const SWIPE_OPEN = 64;
const SWIPE_THRESHOLD = 36;

/** Touch / narrow viewports use swipe-to-delete; desktop uses hover trash. */
function useTouchDeleteMode() {
  const [touchMode, setTouchMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia("(hover: none), (pointer: coarse), (max-width: 1023px)").matches
    );
  });

  useEffect(() => {
    const mq = window.matchMedia("(hover: none), (pointer: coarse), (max-width: 1023px)");
    const update = () => setTouchMode(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  return touchMode;
}

const parseDateValue = (value) => {
  if (!value) return undefined;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

const toDateValue = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Compact due trigger — same calendar popover as DatePickerField. */
function TaskDuePicker({ value, onChange, "data-testid": testId }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseDateValue(value), [value]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "rounded px-1 -mx-0.5 py-0.5 text-[10px] text-[var(--t-meta)]",
            "hover:bg-black/[0.04] hover:text-[var(--t-text)]",
            open && "bg-[rgba(0,30,255,0.04)] text-[var(--t-text)] ring-1 ring-[var(--t-accent)]/35",
          )}
          data-testid={testId}
          aria-label={selected ? `Due ${format(selected, "MMM d, yyyy")}` : "Set due date"}
        >
          {value ? formatDueLabel(value) : "Set due"}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="z-[200] w-auto rounded-[var(--t-event-pill-radius)] p-0 text-[var(--t-text)] t-glass"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            if (!date) return;
            onChange(toDateValue(date));
            setOpen(false);
          }}
          initialFocus
          className="p-4"
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Task row: checkbox · edit title · due picker.
 * Desktop: hover trash. Tablet/mobile: swipe left to reveal trash.
 * No card / white fill — sits flat on the parent surface.
 */
export default function EditableTaskRow({
  task,
  onToggle,
  onUpdate,
  onDelete,
  subtitle = null,
  className = "",
  "data-testid": testId,
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [draft, setDraft] = useState(task.title || "");
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startOffsetRef = useRef(0);
  const axisRef = useRef(null);
  const canDelete = typeof onDelete === "function";
  const touchDelete = useTouchDeleteMode();

  useEffect(() => {
    if (!editingTitle) setDraft(task.title || "");
  }, [task.title, editingTitle]);

  useEffect(() => {
    if (!editingTitle) return undefined;
    const el = inputRef.current;
    if (!el) return undefined;
    el.focus();
    el.select();
    return undefined;
  }, [editingTitle]);

  useEffect(() => {
    // Reset swipe when switching to desktop hover mode.
    if (!touchDelete) setOffset(0);
  }, [touchDelete]);

  const commitTitle = () => {
    const next = String(draft || "").trim();
    setEditingTitle(false);
    if (!next) {
      setDraft(task.title || "");
      return;
    }
    if (next === String(task.title || "").trim()) return;
    onUpdate?.({ title: next });
  };

  const cancelTitle = () => {
    setDraft(task.title || "");
    setEditingTitle(false);
  };

  const closeSwipe = () => setOffset(0);

  const onPointerDown = (e) => {
    if (!canDelete || !touchDelete || editingTitle) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    startOffsetRef.current = offset;
    axisRef.current = null;
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging || !canDelete || !touchDelete) return;
    const dx = e.clientX - startXRef.current;
    const dy = e.clientY - startYRef.current;
    if (!axisRef.current) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      axisRef.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    }
    if (axisRef.current !== "x") return;
    e.preventDefault();
    const next = Math.min(0, Math.max(-SWIPE_OPEN, startOffsetRef.current + dx));
    setOffset(next);
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (axisRef.current !== "x") {
      axisRef.current = null;
      return;
    }
    setOffset((current) => (current <= -SWIPE_THRESHOLD ? -SWIPE_OPEN : 0));
    axisRef.current = null;
  };

  const deleteButton = (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        closeSwipe();
        onDelete?.(task.id);
      }}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-[var(--t-event-pill-radius)] text-[var(--t-meta)] transition-colors",
        "hover:bg-black/[0.04] hover:text-[var(--t-accent)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)]/30",
      )}
      aria-label="Delete task"
      data-testid={testId ? `${testId}-delete` : undefined}
    >
      <Trash2 size={14} strokeWidth={1.5} />
    </button>
  );

  const row = (
    <div
      className={cn(
        "group/task flex w-full items-start gap-2 bg-transparent py-1.5 text-left",
        className,
      )}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => {
          closeSwipe();
          onToggle?.(task.id);
        }}
        className="mt-0.5 shrink-0"
        aria-label={task.done ? "Mark incomplete" : "Mark complete"}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {task.done ? (
          <CheckSquare size={14} className="text-[var(--t-meta)]" strokeWidth={1.4} />
        ) : (
          <Square size={14} className="text-[var(--t-accent)]" strokeWidth={1.4} />
        )}
      </button>

      <div className="min-w-0 flex-1">
        {editingTitle ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelTitle();
              }
            }}
            className={cn(
              "w-full bg-transparent text-sm outline-none",
              "rounded px-1 -mx-1 py-0.5",
              "ring-1 ring-[var(--t-accent)]/40 bg-[rgba(0,30,255,0.03)]",
              task.done && "text-[var(--t-meta)] line-through",
            )}
            data-testid={testId ? `${testId}-title-input` : undefined}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              closeSwipe();
              setEditingTitle(true);
            }}
            className={cn(
              "block w-full truncate rounded px-1 -mx-1 py-0.5 text-left text-sm",
              "hover:bg-black/[0.04] cursor-text",
              task.done && "text-[var(--t-meta)] line-through",
            )}
            data-testid={testId ? `${testId}-title` : undefined}
          >
            {task.title}
          </button>
        )}

        <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 px-1 -mx-1">
          {subtitle ? (
            <>
              <span className="min-w-0 truncate text-[10px] text-[var(--t-meta)]">{subtitle}</span>
              <span className="text-[10px] text-[var(--t-meta)]">·</span>
            </>
          ) : null}
          <TaskDuePicker
            value={task.due || ""}
            onChange={(iso) => {
              const next = String(iso || "").slice(0, 10);
              if (!next || next === String(task.due || "").slice(0, 10)) return;
              closeSwipe();
              onUpdate?.({ due: next });
            }}
            data-testid={testId ? `${testId}-due` : undefined}
          />
        </div>
      </div>

      {canDelete && !touchDelete ? (
        <div className="mt-0.5 opacity-0 transition-opacity group-hover/task:opacity-100 group-focus-within/task:opacity-100">
          {deleteButton}
        </div>
      ) : null}
    </div>
  );

  if (!canDelete) return row;

  // Desktop: hover trash on the row (no swipe chrome).
  if (!touchDelete) return row;

  // Tablet / mobile: swipe left to reveal trash — transparent surface, no white card.
  return (
    <div className="relative overflow-hidden" data-testid={testId ? `${testId}-swipe` : undefined}>
      <div className="absolute inset-y-0 right-0 flex w-16 items-stretch justify-end">
        <button
          type="button"
          onClick={() => {
            closeSwipe();
            onDelete?.(task.id);
          }}
          className="flex w-16 items-center justify-center bg-[var(--t-accent)] text-white"
          aria-label="Delete task"
          data-testid={testId ? `${testId}-delete` : undefined}
        >
          <Trash2 size={16} strokeWidth={1.5} />
        </button>
      </div>
      <div
        className={cn(
          "relative bg-transparent touch-pan-y",
          !dragging && "transition-transform duration-200 ease-out",
        )}
        style={{ transform: `translateX(${offset}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {row}
      </div>
    </div>
  );
}
